<template>
  <v-flex xs12>
    <v-card elevation="15">
      <v-card-title primary-title class="h3">
        Meddelanden
      </v-card-title>       
      <v-card-text>
        <v-form ref="form"
                v-model="valid"
                lazy-validation
        >
          <v-container>
            <v-row>
              <v-col cols="4">
                <v-select
                  v-model="message.type"
                  :items="types"
                  item-text="description"
                  item-value="id"
                  label="Typ"
                  :rules="[v => !!v || 'Måste välja typ!']"
                  required
                />
              </v-col>
            </v-row>
            <v-row v-if="[ 'CUSTOMER_INVOICE'].includes(messageTypeTag(message.type))">
              <v-col cols="4">
                <v-select
                  v-model="message.ledger"
                  :items="paperOptions"
                  item-text="label"
                  item-value="value"
                  label="Visas på faktura för"  
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col v-if="['DELIVERY_INFO', 'DELIVERY', 'CUSTOMER_INVOICE'].includes(messageTypeTag(message.type))" cols="4">
                <v-dialog
                  ref="dialog"
                  v-model="dateModal"
                  :return-value.sync="message.date"
                  persistent
                  width="290px"                                    
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="messageDateDisplay"
                      label="Datum"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="datePickerValue"
                    :type="messageTypeTag(message.type) === 'CUSTOMER_INVOICE' ? 'month' : 'date'"
                    scrollable
                    locale="sv-se"
                    :first-day-of-week="1"                    
                  >
                    <v-spacer />
                    <v-btn
                      text
                      color="primary"
                      @click="dateModal = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.dialog.save(message.date)"
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col v-else-if="['CUSTOMER', 'RETURN'].includes(messageTypeTag(message.type))" cols="4">
                <RangePicker :date-range="message.dates" :rules="dateRangeRules"
                             @selected="selected"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="message.header" label="Rubrik" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <editor
                  id="tinyEditor"
                  v-model="message.content"
                  api-key="d47ymhcw9hvtg6s6nwtded1xoirdwf88ndckstyg7acxf9xt" 
                  :toolbar="toolbar" 
                  :plugins="plugins"      
                  :init="init"                
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col v-show="['CUSTOMER', 'RETURN', 'CUSTOMER_INVOICE'].includes(messageTypeTag(message.type))" cols="6">
                <v-text-field
                  v-model="kundnr"
                  label="Kunder"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-btn
            :disabled="!valid"
            color="success"
            class="mr-4"
            @click="save"
          >
            <span v-if="message.id == 0">Spara</span>
            <span v-else>Uppdatera</span>            
          </v-btn>
          <v-btn 
            color="warning"
            @click="resetForm"
          >
            Nollställ
          </v-btn>
        </v-form>        
      </v-card-text>
      <v-card-text>
        <v-card-title>Sparade meddelanden</v-card-title>
        <v-simple-table>
          <template>
            <thead>
              <tr>
                <th class="text-left">
                  Rubrik
                </th>
                <th class="text-left">
                  Text
                </th>
                <th class="text-left">
                  Datum
                </th>
                <th class="text-left">
                  Typ
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item,idx) in messages"
                :key="item.id"
                @click="edit(idx)"
              >
                <td>{{ item.header }}</td>
                <td v-html="item.content" />
                <td v-if="['DELIVERY_INFO', 'DELIVERY'].includes(messageTypeTag(item.type))">
                  {{ item.startDate|toLocalDate }}
                </td>
                <td v-else>
                  {{ item.startDate |toLocalDate }} - {{ item.stopDate |toLocalDate }}
                </td>
                <td>{{ messageType(item.type) }}</td>
                <td>
                  <v-btn x-small icon
                         @click.stop="remove(idx)"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </v-flex>
</template>   
<script>
import api from '@/backend/adminapi'
import log from '@/logger'
import Editor from '@tinymce/tinymce-vue'
import RangePicker from '../../components/web/RangePicker.vue'
import {orderBy} from 'lodash'

const PAPER_OPTIONS = [
  { value: null, label: 'Båda (Aftonbladet & Expressen)' },
  { value: 83, label: 'Aftonbladet' },
  { value: 80, label: 'Expressen' },
]

export default {    
  name: 'Message',
  components: { 'editor': Editor,  RangePicker },
  props: {
  toolbar: {
      type:String,
      default:'undo redo | paste | bold italic underline link | \
       | removeformat | '
    },    
    plugins: {
      type: Array,
      default: function() {
        return [
        'autolink lists link ',
        'searchreplace visualblocks fullscreen',
        'insertdatetime paste code help wordcount'
      ]}
    }
  },
  data() {
    return {
      messages: [],
      loading: false,
      valid: true,
      dateModal:false,
      paperOptions: PAPER_OPTIONS,
      types:[
            {id:1,description:'Leveransinformation',tag:'DELIVERY_INFO'},
            {id:2,description:'Följesedel',tag:'DELIVERY'},
            {id:3,description:'Kundmeddelande',tag:'CUSTOMER'},
            {id:4,description:'Returrapportering',tag:'RETURN'},
            {id:5,description:'Kundmeddelande faktura',tag:'CUSTOMER_INVOICE'}
        ],
      message:{
            id:0,
            type:0,
            date:(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10),
            dates:[],
            header:'',
            content:'' ,
            ledger:null           
        },
      kundnr:'',
      init: {       
        height: 200,
        width: 768,
        menubar: false,                       
        paste_data_images: false,         
        forced_root_block : '',       
      },     
      dateRangeRules: [
        v => !!v || 'Datum ej ifyllt is',
        v => (v && v[0] > v[1]) || 'Perioden felaktig',
      ],
    }
  },
  computed:{
    dateRangeText() {
      return this.message.dates.join(' - ')
    },       
    datePickerValue: {
      get() {
        return this.messageTypeTag(this.message.type) === 'CUSTOMER_INVOICE'
          ? this.message.date.substring(0, 7)
          : this.message.date
      },
      set(value) {
        this.message.date = this.messageTypeTag(this.message.type) === 'CUSTOMER_INVOICE'
          ? `${value}-01`
          : value
      },
    },
    messageDateDisplay() {
      if (this.messageTypeTag(this.message.type) !== 'CUSTOMER_INVOICE') {
        return this.message.date
      }
      const [year, month] = this.message.date.split('-')
      const monthName = new Intl.DateTimeFormat('sv-SE', { month: 'short', timeZone: 'UTC' })
        .format(new Date(Date.UTC(Number(year), Number(month) - 1, 1)))
        .replace(/\.$/, '')
      return `${year}-${monthName.toUpperCase()}`
    },
   
  },
  mounted() {
    this.loadData()
  },
  methods: {   
    loadData() {
      console.log('Get messages')
      var self = this
      api
        .adminGetMessages()
        .then((resp) => {
          console.log(resp.data)
          this.loading = false
          this.messages = orderBy(resp.data, ['startDate'], ['desc'])
        })
       
        .catch((err) => {
          if (
            (err.response && err.response.status === 401) ||
            err.response.status === 403
          ) {
            console.log(err)
            self.loading = false
            self.$router.push({ name: 'root' })
          } else if (err.response && err.response.status === 404) {
            console.log(err)
            self.loading = false
          }
        })
    },
    selected(dates){
      this.message.dates = dates
    },
    validate () {
      this.$refs.form.validate()        
      },
    resetForm(){        
      this.message.id =0
      this.message.type=0
      this.message.date=(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
      this.message.dates=[]
      this.message.header=''
      this.message.content=''
      this.message.ledger = null
      this.kundnr = ''
    },
    edit(idx){      
      var message = this.messages.at(idx)
      log.debug(message)
      this.message = Object.assign({}, message)      
      this.message.header=this.htmlDecode(message.header)
      this.message.date=this.$options.filters.toLocalDate(message.startDate)
      this.message.dates=[this.$options.filters.toLocalDate(message.startDate),this.$options.filters.toLocalDate(message.stopDate)]
      if(['CUSTOMER', 'RETURN', 'CUSTOMER_INVOICE'].includes(this.messageTypeTag(this.message.type)))
      {
        var self = this
        api.adminGetCustomersForMessage(this.message.id)
        .then(resp=>{
          self.kundnr = resp.data
        })
        .catch(error => {
          self.loading = false
          log.debug('Errors:', error)
        })       
      }
    },
    remove(idx){      
      var message = this.messages.at(idx)
      var self = this
      var msg = {
        id:message.id,
        content:message.content,
        header:message.header,
        startDate:message.startDate,
        stopDate:message.stopDate,
        type:message.type,
        ledger:message.ledger,
      }
      log.debug(msg)
      api.adminDeleteMessage(msg).then(()=>{
          self.loading = false          
          self.messages.splice(idx,1)
          log.debug('Deleted:', message)
          this.resetForm()
          //this.$emit('saved', paper)
      })
        .catch(error => {
          self.loading = false
          log.debug('Errors:', error)
        })        
    },
    save(){
     this.validate()
      log.debug('Save message ',this.message)
      var self = this
      var update = false
      self.loading = true
      var msg ={
        header:this.htmlEncode(this.message.header),
        content:this.message.content,
        type:this.message.type,
        ledger:this.message.ledger
      }
      
      if(this.message.id > 0){
        msg.id= this.message.id
        update = true
      }
      
      if(this.messageTypeTag(this.message.type) === 'CUSTOMER_INVOICE'){
        const [year, month] = this.message.date.split('-')
        const lastDay = new Date(Number(year), Number(month), 0).getDate()
        msg.startDate = `${year}-${month}-01`
        msg.stopDate = `${year}-${month}-${String(lastDay).padStart(2, '0')} 23:59:59`
      }
      else if(['DELIVERY_INFO', 'DELIVERY'].includes(this.messageTypeTag(this.message.type))){
        msg.startDate = this.message.date
        msg.stopDate = this.message.date
      }
      else{
        msg.startDate = this.message.dates[0]
        msg.stopDate = this.message.dates[1]
      }
      api
        .adminSaveMessage(msg)
        .then(resp => {
          self.loading = false
          var message = resp.data          
          self.message.id = message.id
          if(!update){
            self.messages.push(message)
          }
          else{
            
            var index = this.messages.findIndex(obj => obj.id === message.id)
            if(index !== -1 ){
              this.messages[index] = message
            }else{
              log.debug(`Could not find ${message.id} in messages.`)
            }          
          }
          log.debug('Success:', message)                        
        })
        .then(()=>{
          self.kundnr = self.kundnr.replace(/\s+/g, '')
          if(['CUSTOMER', 'RETURN', 'CUSTOMER_INVOICE'].includes(self.messageTypeTag(self.message.type))){
            api.adminSaveMessageToCustomer(self.message.id, self.kundnr,self.message.ledger)
            .then(()=>{              
                this.resetForm()
            })
            .catch(error => {          
              log.debug('Errors:', error)
            }) 
          }
        })
        .then(()=>{
          if(self.kundnr.length === 0){
            this.resetForm()
          }
        })
        .catch(error => {
          self.loading = false
          log.debug('Errors:', error)
        })        
    },
    messageType(typeId){
      var obj = this.types.find((type) => type.id == typeId)
      return obj.description
    },
    messageTypeTag(typeId){
      var obj = this.types.find((type) => type.id == typeId)
      return obj && obj.tag
    },
    htmlEncode(str) {
      if (!str) return ''
      const textarea = document.createElement('textarea')
      textarea.textContent = str
      return textarea.innerHTML
    }    ,
     htmlDecode(str) {
      if (!str) return ''
      const textarea = document.createElement('textarea')
      textarea.innerHTML = str
      return textarea.value
    }
      
  },
}
</script>

<style></style>
